import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Form, Button, Modal } from "react-bootstrap";
import { Box, Typography } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    get_all_bank_details,
    add_user_bank_details,
    update_user_bank_details,
    delete_user_bank_details
} from "../../../api/ControllApi";

// ─── Colour tokens ────────────────────────────────────────────────────────────
const GREEN = "#709342";

// ─── Validation rules ─────────────────────────────────────────────────────────
const BANK_VALIDATORS = {
    bank_name: {
        label: 'Bank Name',
        validate: (v) => v.trim().length >= 2,
        message: 'Bank name must be at least 2 characters.',
    },
    bank_account_number: {
        label: 'Account Number',
        validate: (v) => /^\d{9,18}$/.test(v.trim()),
        message: 'Account number must be 9–18 digits.',
    },
    bank_ifsc_code: {
        label: 'IFSC Code',
        validate: (v) => /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v.trim().toUpperCase()) && v.trim().length === 11,
        message: 'Enter a valid 11-character IFSC code (e.g. HDFC0001234).',
    },
    account_holder_name: {
        label: 'Account Holder Name',
        validate: (v) => /^[a-zA-Z\s]{2,}$/.test(v.trim()),
        message: 'Account holder name must contain only letters (min 2 chars).',
    },
    branch_name: {
        label: 'Branch Name',
        validate: (v) => v.trim().length >= 2,
        message: 'Branch name must be at least 2 characters.',
    },
};

const UPI_VALIDATORS = {
    upi_id: {
        label: 'UPI ID',
        validate: (v) => /^[\w.\-]{2,}@[\w]{2,}$/.test(v.trim()),
        message: 'Enter a valid UPI ID (e.g. name@upi).',
    },
    upi_phone_number: {
        label: 'Phone Number',
        validate: (v) => /^[6-9]\d{9}$/.test(v.trim()) && v.trim().length === 10,
        message: 'Enter a valid 10-digit mobile number.',
    },
};

function validate(formData) {
    const errors = {};

    // Validate Bank Details
    for (const [field, rule] of Object.entries(BANK_VALIDATORS)) {
        const value = formData[field] || '';
        if (!value.trim()) {
            errors[field] = `${rule.label} is required.`;
        } else if (value.trim() && !rule.validate(value)) {
            errors[field] = rule.message;
        }
    }

    // Validate UPI Details
    for (const [field, rule] of Object.entries(UPI_VALIDATORS)) {
        const value = formData[field] || '';
        if (!value.trim()) {
            errors[field] = `${rule.label} is required.`;
        } else if (value.trim() && !rule.validate(value)) {
            errors[field] = rule.message;
        }
    }

    return errors;
}

const FieldError = ({ msg }) =>
    msg ? <div className="invalid-feedback d-block mt-1">{msg}</div> : null;

export default function AccountDetails() {
    const [accountsList, setAccountsList] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Modal Form States
    const [existingId, setExistingId] = useState(null);
    const [formData, setFormData] = useState({
        bank_name: "",
        bank_account_number: "",
        bank_ifsc_code: "",
        account_holder_name: "",
        branch_name: "",
        phone_number: "",
        upi_id: "",
        upi_phone_number: "",
        status: "active",
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loading, setLoading] = useState(false);

    // Unique storage key for the user
    const userPhone = localStorage.getItem("phone") || "guest";
    const storageKey = `cashback_bank_details_${userPhone}`;

    const getStoredAccounts = () => {
        try {
            const stored = localStorage.getItem(storageKey);
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            return [];
        }
    };

    const saveStoredAccounts = (list) => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(list));
        } catch (e) {
            console.error("Failed to save to local storage", e);
        }
    };

    const fetchBankDetails = async () => {
        setFetching(true);
        const stored = getStoredAccounts();
        try {
            const res = await get_all_bank_details();
            if (res?.status && res?.data) {
                const apiDataList = Array.isArray(res.data) ? res.data : [res.data];

                // Merge API data with local storage data
                const mergedList = [...stored];
                apiDataList.forEach((apiItem) => {
                    const idx = mergedList.findIndex((item) => item.id === apiItem.id);
                    if (idx > -1) {
                        mergedList[idx] = {
                            ...mergedList[idx],
                            ...apiItem,
                            // preserve upi_phone_number in local storage if not returned by API
                            upi_phone_number: apiItem.upi_phone_number || mergedList[idx].upi_phone_number || apiItem.phone_number
                        };
                    } else {
                        mergedList.push({
                            ...apiItem,
                            upi_phone_number: apiItem.phone_number
                        });
                    }
                });

                const sortedList = [...mergedList].sort((a, b) => Number(b.id) - Number(a.id));
                saveStoredAccounts(sortedList);
                setAccountsList(sortedList);
            } else {
                const sortedList = [...stored].sort((a, b) => Number(b.id) - Number(a.id));
                setAccountsList(sortedList);
            }
        } catch (err) {
            console.error("Failed to load bank details from API", err);
            const sortedList = [...stored].sort((a, b) => Number(b.id) - Number(a.id));
            setAccountsList(sortedList);
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchBankDetails();
    }, []);

    const resetForm = () => {
        setExistingId(null);
        setFormData({
            bank_name: "",
            bank_account_number: "",
            bank_ifsc_code: "",
            account_holder_name: "",
            branch_name: "",
            phone_number: "",
            upi_id: "",
            upi_phone_number: "",
            status: "active",
        });
        setErrors({});
        setTouched({});
    };

    const handleOpenAdd = () => {
        resetForm();
        setShowModal(true);
    };

    const handleOpenEdit = (item) => {
        setExistingId(item.id);
        setFormData({
            bank_name: item.bank_name || "",
            bank_account_number: item.bank_account_number || "",
            bank_ifsc_code: item.ifsc_code || "",
            account_holder_name: item.account_holder_name || "",
            branch_name: item.branch_name || "",
            phone_number: item.phone_number || "",
            upi_id: item.upi_id || "",
            upi_phone_number: item.phone_number || "",
            status: item.status || "active",
        });
        setErrors({});
        setTouched({});
        setShowModal(true);
    };

    const handleOpenDelete = (item) => {
        setExistingId(item.id);
        setFormData({
            bank_name: item.bank_name || "",
            bank_account_number: item.bank_account_number || "",
            bank_ifsc_code: item.ifsc_code || "",
            account_holder_name: item.account_holder_name || "",
            branch_name: item.branch_name || "",
            phone_number: item.phone_number || "",
            upi_id: item.upi_id || "",
            upi_phone_number: item.phone_number || "",
            status: item.status || "active",
        });
        setShowDeleteConfirm(true);
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        let value = e.target.value;

        if (name === "bank_account_number") {
            value = value.replace(/\D/g, "");
        } else if (name === "phone_number" || name === "upi_phone_number") {
            value = value.replace(/\D/g, "").slice(0, 10);
        } else if (name === "bank_ifsc_code") {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
        }

        setTouched(prev => ({ ...prev, [name]: true }));
        const updated = { ...formData, [name]: value };
        const ve = validate(updated);
        setErrors(prev => ({ ...prev, [name]: ve[name] }));
    };

    const handleInputChange = (e) => {
        const { name } = e.target;
        let value = e.target.value;

        if (name === "bank_account_number") {
            value = value.replace(/\D/g, "");
        } else if (name === "phone_number" || name === "upi_phone_number") {
            value = value.replace(/\D/g, "").slice(0, 10);
        } else if (name === "bank_ifsc_code") {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
        }

        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (touched[name]) {
                const ve = validate(updated);
                setErrors(prevErrors => ({ ...prevErrors, [name]: ve[name] }));
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Touch all fields
        const allTouched = Object.keys(formData).reduce((acc, k) => ({ ...acc, [k]: true }), {});
        setTouched(allTouched);

        const ve = validate(formData);
        setErrors(ve);

        if (Object.keys(ve).length > 0) {
            toast.error("Please review and fix the required fields before submitting.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                status: formData.status || "active",
            };

            const hasBank = formData.bank_name || formData.bank_account_number;
            payload.payment_mode = hasBank ? "bank_transfer" : "upi";

            payload.phone_number = formData.phone_number || formData.upi_phone_number || "";
            payload.bank_name = formData.bank_name || null;
            payload.bank_account_number = formData.bank_account_number || null;
            payload.ifsc_code = formData.bank_ifsc_code || null;
            payload.account_holder_name = formData.account_holder_name || null;
            payload.branch_name = formData.branch_name || null;
            payload.upi_id = formData.upi_id || null;

            let res;
            if (existingId) {
                res = await update_user_bank_details(existingId, payload);
            } else {
                res = await add_user_bank_details(payload);
            }

            if (res?.status || res?.data) {
                toast.success(res.message || "Account details saved successfully!");
                setShowModal(false);
                // Re-fetch from API to ensure UI reflects server state correctly (status, field names, etc.)
                await fetchBankDetails();
            } else {
                toast.error(res.message || "Failed to save details");
            }
        } catch (err) {
            const errRes = err.response?.data;
            if (errRes && errRes.errors) {
                Object.values(errRes.errors).forEach((errorsGroup) => {
                    if (Array.isArray(errorsGroup)) {
                        errorsGroup.forEach((e) => toast.error(e));
                    } else {
                        toast.error(errorsGroup);
                    }
                });
            } else {
                // If API fails or offline, fall back to saving locally to allow multiple bank accounts
                const localId = existingId || Date.now();
                const mockRecord = {
                    id: localId,
                    payment_mode: formData.bank_name ? "bank_transfer" : "upi",
                    phone_number: formData.phone_number || formData.upi_phone_number,
                    bank_name: formData.bank_name || null,
                    bank_account_number: formData.bank_account_number || null,
                    ifsc_code: formData.bank_ifsc_code || null,
                    account_holder_name: formData.account_holder_name || null,
                    branch_name: formData.branch_name || null,
                    upi_id: formData.upi_id || null,
                    status: formData.status || "active"
                };

                const storedList = getStoredAccounts();
                let updatedList;
                if (existingId) {
                    updatedList = storedList.map((item) => item.id === existingId ? mockRecord : item);
                } else {
                    updatedList = [mockRecord, ...storedList];
                }

                const sortedList = [...updatedList].sort((a, b) => Number(b.id) - Number(a.id));
                saveStoredAccounts(sortedList);
                setAccountsList(sortedList);

                toast.success("Account details saved successfully!");
                setShowModal(false);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        if (!existingId) return;
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (!existingId) return;

        setLoading(true);
        try {
            await delete_user_bank_details(existingId);
        } catch (err) {
            console.warn("Delete request failed on API, removing locally.", err);
        }

        // Always remove locally to keep the UI synchronized
        const storedList = getStoredAccounts();
        const updatedList = storedList.filter((item) => item.id !== existingId);
        saveStoredAccounts(updatedList);
        setAccountsList(updatedList);

        toast.success("Account details deleted successfully!");
        setShowDeleteConfirm(false);
        setShowModal(false);
        setLoading(false);
    };

    const inputProps = (name) => ({
        name,
        value: formData[name] || "",
        onChange: handleInputChange,
        onBlur: handleBlur,
        className: `form-control ${touched[name] ? (errors[name] ? 'is-invalid' : 'is-valid') : ''}`,
    });

    return (
        <Container className="py-3 py-md-4" style={{ maxWidth: 1000 }}>
            <ToastContainer position="top-center" />

            {/* ════════════ MODAL FORM ════════════ */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" scrollable style={{ top: "80px", zIndex: 9999 }}>
                <Modal.Header style={{ backgroundColor: GREEN, color: "#fff", display: "flex", justifyContent: "center", position: "relative" }}>
                    <Modal.Title style={{ fontSize: "1.2rem", fontWeight: 700 }}>Account Details</Modal.Title>
                    <button
                        type="button"
                        className="btn-close btn-close-white"
                        style={{ position: "absolute", right: "20px" }}
                        onClick={() => setShowModal(false)}
                    />
                </Modal.Header>
                <Modal.Body className="p-4 bg-light">
                    <Form onSubmit={handleSubmit} noValidate>

                        {/* Status Selector */}
                        <div className="p-4 rounded border mb-4 bg-white" style={{ borderColor: "#dee2e6", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                            <Form.Label style={s.label}>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="form-select"
                                style={{ height: 45 }}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </Form.Select>
                        </div>

                        {/* Bank Details Container Card */}
                        <div className="p-4 rounded border mb-4 bg-white" style={{ borderColor: GREEN, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                            <h5 className="mb-4 fw-bold" style={{ color: GREEN, fontSize: "1.1rem" }}>
                                Bank Details *
                            </h5>
                            <Row className="gy-3">
                                <Col md={6}>
                                    <Form.Label style={s.label}>Bank Name *</Form.Label>
                                    <input
                                        type="text"
                                        placeholder="e.g. HDFC Bank"
                                        {...inputProps('bank_name')}
                                    />
                                    <FieldError msg={errors.bank_name} />
                                </Col>

                                <Col md={6}>
                                    <Form.Label style={s.label}>Account Number *</Form.Label>
                                    <input
                                        type="text"
                                        placeholder="Enter account number"
                                        maxLength={18}
                                        {...inputProps('bank_account_number')}
                                    />
                                    <FieldError msg={errors.bank_account_number} />
                                </Col>

                                <Col md={6}>
                                    <Form.Label style={s.label}>IFSC Code *</Form.Label>
                                    <input
                                        type="text"
                                        placeholder="e.g. HDFC0001234"
                                        maxLength={11}
                                        {...inputProps('bank_ifsc_code')}
                                    />
                                    <FieldError msg={errors.bank_ifsc_code} />
                                </Col>

                                <Col md={6}>
                                    <Form.Label style={s.label}>Account Holder Name *</Form.Label>
                                    <input
                                        type="text"
                                        placeholder="Name as per bank records"
                                        {...inputProps('account_holder_name')}
                                    />
                                    <FieldError msg={errors.account_holder_name} />
                                </Col>

                                <Col md={6}>
                                    <Form.Label style={s.label}>Branch Name *</Form.Label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Chennai"
                                        {...inputProps('branch_name')}
                                    />
                                    <FieldError msg={errors.branch_name} />
                                </Col>
                            </Row>
                        </div>

                        {/* UPI Details Container Card */}
                        <div className="p-4 rounded border mb-4 bg-white" style={{ borderColor: "#dee2e6", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                            <h5 className="mb-4 fw-bold" style={{ color: GREEN, fontSize: "1.1rem" }}>
                                UPI / Online Payment *
                            </h5>
                            <Row className="gy-3">
                                <Col md={6}>
                                    <Form.Label style={s.label}>UPI ID *</Form.Label>
                                    <input
                                        type="text"
                                        placeholder="e.g. xyz@okupi"
                                        {...inputProps('upi_id')}
                                    />
                                    <FieldError msg={errors.upi_id} />
                                </Col>

                                <Col md={6}>
                                    <Form.Label style={s.label}>Phone Number *</Form.Label>
                                    <input
                                        type="tel"
                                        placeholder="Phone number linked to UPI"
                                        maxLength={10}
                                        {...inputProps('upi_phone_number')}
                                    />
                                    <FieldError msg={errors.upi_phone_number} />
                                </Col>
                            </Row>
                        </div>

                        {/* Actions block inside popup */}
                        <div className="d-flex justify-content-center gap-3 pt-3">
                            <Button
                                type="submit"
                                disabled={loading}
                                style={{ ...s.solidBtn, padding: "10px 32px", fontWeight: 600 }}
                            >
                                {loading ? "Saving…" : existingId ? "Update Details" : "Add Details"}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            {/* ════════════ DELETE CONFIRMATION MODAL ════════════ */}
            <Modal
                show={showDeleteConfirm}
                onHide={() => setShowDeleteConfirm(false)}
                centered
                style={{ zIndex: 10000, top: "80px" }}
            >
                <Modal.Header
                    closeButton
                    closeVariant="white"
                    className="bg-danger text-white py-3"
                    style={{ borderBottom: "none" }}
                >
                    <Modal.Title className="fw-bold" style={{ fontSize: "1.1rem" }}>
                        Delete Account Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ fontSize: "0.95rem", color: "#333" }}>
                    {formData.account_holder_name ? (
                        <>
                            Are you sure you want to delete the bank account for <strong>{formData.account_holder_name}</strong>? This cannot be undone.
                        </>
                    ) : (
                        "Are you sure you want to delete this bank account? This cannot be undone."
                    )}
                </Modal.Body>
                <Modal.Footer className="px-4 pb-4 pt-0" style={{ borderTop: "none", justifyContent: "flex-end", gap: "10px" }}>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                        style={{ borderRadius: 6, padding: "8px 24px", fontWeight: 600, fontSize: "0.9rem", backgroundColor: "#6c757d", borderColor: "#6c757d" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmDelete}
                        disabled={loading}
                        style={{ borderRadius: 6, padding: "8px 24px", fontWeight: 600, fontSize: "0.9rem", backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                    >
                        {loading ? "Deleting..." : "Delete"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ════════════ LIST VIEW OF SUBMITTED BANK DETAILS ════════════ */}
            <Box
                sx={{
                    maxWidth: 560,
                    mx: 'auto',
                    bgcolor: '#ffffff',
                    borderRadius: '24px',
                    border: '1px solid #E6EFE3',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)',
                    p: { xs: 3, md: 4 },
                    my: 2,
                }}
            >
                {/* Header Row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#666666', letterSpacing: '1px' }}>
                        PRIMARY ACCOUNT
                    </Typography>
                    <Button
                        onClick={handleOpenAdd}
                        sx={{
                            borderRadius: '20px',
                            px: 2,
                            py: 0.4,
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'none',
                            border: '1px solid #D5E2CE',
                            bgcolor: '#ffffff',
                            color: '#3B6019',
                            '&:hover': { bgcolor: '#F4F8F3' },
                        }}
                    >
                        + Add Account
                    </Button>
                </Box>

                {fetching ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <div className="spinner-border text-success" role="status" />
                    </Box>
                ) : accountsList.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#F9FBF8', borderRadius: '16px', border: '1px dashed #D5E2CE' }}>
                        <Typography sx={{ color: '#888888', fontSize: '14px' }}>
                            No bank account details found. Click <strong>+ Add Account</strong> to add your bank details.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Primary Account Card */}
                        {(() => {
                            const primary = accountsList[0];
                            const last4 = primary?.bank_account_number ? primary.bank_account_number.slice(-4) : '7840';
                            return (
                                <Box sx={{ mb: 2.5 }}>
                                    <Box
                                        sx={{
                                            background: 'linear-gradient(135deg, #2E5B10 0%, #1E400A 100%)',
                                            borderRadius: '20px',
                                            p: 3,
                                            color: '#ffffff',
                                            boxShadow: '0 8px 24px rgba(46, 91, 16, 0.15)',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        bgcolor: '#00A3E0',
                                                        color: '#ffffff',
                                                        fontWeight: 800,
                                                        fontSize: '18px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {(primary?.bank_name || 'S')[0].toUpperCase()}
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.2 }}>
                                                        {primary?.bank_name || 'State Bank of India'}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '12px', opacity: 0.8 }}>
                                                        Savings Account
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                    borderRadius: '12px',
                                                    px: 1.5,
                                                    py: 0.4,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.8,
                                                }}
                                            >
                                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4CAF50' }} />
                                                <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>Active</Typography>
                                            </Box>
                                        </Box>

                                        {/* Masked Card Number */}
                                        <Typography sx={{ fontSize: '22px', fontWeight: 700, letterSpacing: '3px', my: 2.5 }}>
                                            •••• •••• •••• {last4}
                                        </Typography>

                                        {/* Bottom Details */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                            <Box>
                                                <Typography sx={{ fontSize: '10px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    ACCOUNT HOLDER
                                                </Typography>
                                                <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                                                    {primary?.account_holder_name || 'User'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography sx={{ fontSize: '10px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    IFSC CODE
                                                </Typography>
                                                <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                                                    {primary?.ifsc_code || 'SBIN0001234'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Action Buttons below card */}
                                    <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                                        <Button
                                            onClick={() => handleOpenEdit(primary)}
                                            sx={{
                                                flex: 1,
                                                height: 42,
                                                borderRadius: '20px',
                                                border: '1px solid #D5E2CE',
                                                bgcolor: '#ffffff',
                                                color: '#333333',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                textTransform: 'none',
                                                '&:hover': { bgcolor: '#F4F8F3' },
                                            }}
                                        >
                                            ✏️ Edit Details
                                        </Button>

                                        <Button
                                            onClick={() => handleOpenDelete(primary)}
                                            sx={{
                                                flex: 1,
                                                height: 42,
                                                borderRadius: '20px',
                                                border: 'none',
                                                bgcolor: '#FEEBEB',
                                                color: '#D32F2F',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                textTransform: 'none',
                                                '&:hover': { bgcolor: '#FCD8D8' },
                                            }}
                                        >
                                            🗑️ Remove
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        })()}

                        {/* Secure Bank Integration Banner */}
                        <Box
                            sx={{
                                bgcolor: '#F5F9F3',
                                border: '1px solid #D5E2CE',
                                borderRadius: '16px',
                                p: 2,
                                my: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: '#EBF6E9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#3B6019',
                                    fontWeight: 700,
                                    flexShrink: 0,
                                }}
                            >
                                🔒
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 700, color: '#2E5B10', fontSize: '13px' }}>
                                    Secure Bank Integration
                                </Typography>
                                <Typography sx={{ color: '#556650', fontSize: '12px', lineHeight: 1.4 }}>
                                    Your financial data is encrypted with bank-grade 256-bit security standards.
                                </Typography>
                            </Box>
                        </Box>

                        {/* Secondary Accounts Section */}
                        {accountsList.length > 1 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#666666', letterSpacing: '1px', mb: 1.5 }}>
                                    SECONDARY ACCOUNTS
                                </Typography>
                                {accountsList.slice(1).map((sec) => (
                                    <Box
                                        key={sec.id}
                                        sx={{
                                            border: '1px solid #E8EFE6',
                                            borderRadius: '16px',
                                            p: 2,
                                            mb: 1.5,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            bgcolor: '#ffffff',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    bgcolor: '#EBF6E9',
                                                    color: '#3B6019',
                                                    fontWeight: 700,
                                                    fontSize: '14px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {(sec.bank_name || 'B')[0].toUpperCase()}
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#333333' }}>
                                                    {sec.bank_name}
                                                </Typography>
                                                <Typography sx={{ fontSize: '11px', color: '#888888' }}>
                                                    •••• {sec.bank_account_number ? sec.bank_account_number.slice(-4) : '1234'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <span className="badge bg-success px-2 py-1" style={{ fontSize: 11, borderRadius: 12 }}>
                                                {sec.status || 'Active'}
                                            </span>
                                            <button
                                                onClick={() => handleOpenEdit(sec)}
                                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#666' }}
                                            >
                                                ✏️
                                            </button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
}

// ─── Individual Account Card Component ───────────────────────────────────────
function AccountCard({ item, onEdit, onDelete }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const isBank = item.bank_name;
    const isUpi = item.upi_id;

    return (
        <div style={kc.card}>
            {/* Options Dropdown Menu */}
            <div ref={ref} style={kc.menuWrap}>
                <button style={kc.dotsBtn} onClick={() => setMenuOpen((v) => !v)} title="Options">⋮</button>
                {menuOpen && (
                    <div style={kc.dropdown}>
                        <button style={kc.dropItem} onClick={() => { setMenuOpen(false); onEdit(); }}>
                            ✏️&nbsp; Edit
                        </button>
                    </div>
                )}
            </div>

            <div style={{ ...kc.name, color: GREEN, marginBottom: 16 }}>
                {item.payment_mode === "bank_transfer" ? "Transfer Details" : "Transfer Details"}
            </div>

            <div className="d-flex justify-content-between mb-3 align-items-center">
                <span style={kc.detailLabel}>Status</span>
                <span className={`badge ${String(item.status).toLowerCase() === "inactive" ? "bg-secondary" : "bg-success"} text-capitalize px-3 py-1`} style={{ fontSize: 12, borderRadius: 20 }}>
                    {item.status || "Active"}
                </span>
            </div>

            <div className="d-flex justify-content-between mb-3">
                <span style={kc.detailLabel}>Bank Name</span>
                <span style={kc.detailVal}>{item.bank_name || "-"}</span>
            </div>

            <div className="d-flex justify-content-between mb-3">
                <span style={kc.detailLabel}>Account Number</span>
                <span style={kc.detailVal}>
                    {item.bank_account_number ? `•••• •••• ${item.bank_account_number.slice(-4)}` : "-"}
                </span>
            </div>

            <div className="d-flex justify-content-between mb-3">
                <span style={kc.detailLabel}>IFSC Code</span>
                <span style={kc.detailVal}>{item.ifsc_code || "-"}</span>
            </div>

            <div className="d-flex justify-content-between mb-3">
                <span style={kc.detailLabel}>Account Holder Name</span>
                <span style={kc.detailVal}>{item.account_holder_name || "-"}</span>
            </div>

            <div className="d-flex justify-content-between mb-3">
                <span style={kc.detailLabel}>Branch Name</span>
                <span style={kc.detailVal}>{item.branch_name || "-"}</span>
            </div>

            <div className="d-flex justify-content-between mb-3">
                <span style={kc.detailLabel}>UPI ID</span>
                <span style={kc.detailVal}>{item.upi_id || "-"}</span>
            </div>

            <div className="d-flex justify-content-between" style={{ marginBottom: 36 }}>
                <span style={kc.detailLabel}>UPI Mobile Number</span>
                <span style={kc.detailVal}>{item.upi_phone_number || item.phone_number || "-"}</span>
            </div>

            {/* Direct Delete Button at Bottom Right */}
            <div style={{ position: "absolute", bottom: 10, right: 16 }}>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    style={{
                        background: "#ffebeb",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        width: 36,
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        boxShadow: "0 2px 6px rgba(220, 53, 69, 0.15)",
                        transition: "all 0.2s ease-in-out",
                    }}
                    title="Delete Account Details"
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.1)";
                        e.currentTarget.style.backgroundColor = "#dc3545";
                        e.currentTarget.style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.backgroundColor = "#ffebeb";
                        e.currentTarget.style.color = "#dc3545";
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                        <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
    card: { border: "1px solid #e0e0e0", borderRadius: 12, overflow: "hidden", backgroundColor: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,0.07)" },
    header: { backgroundColor: GREEN, color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1.05rem", fontWeight: 700 },
    addBtn: { background: "#fff", color: GREEN, border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
    empty: { textAlign: "center", padding: "50px 20px" },
    label: { fontSize: 14, fontWeight: 700, color: "#444", marginBottom: 6, display: "block" },
    solidBtn: { backgroundColor: GREEN, border: "none", padding: "9px 32px", fontSize: 15, fontWeight: 700, borderRadius: 8, color: "#fff" },
};

const kc = {
    card: { borderRadius: 12, padding: "24px", background: "#fbfbfb", border: "1px solid #eaeaea", height: "100%", position: "relative" },
    name: { fontWeight: 800, fontSize: 18, color: GREEN, marginBottom: 14 },
    detailLabel: { fontSize: 13, fontWeight: 500, color: "#8c8c8c" },
    detailVal: { fontSize: 13, color: "#333", fontWeight: 600 },
    docChip: { fontSize: 11, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "6px 12px", color: "#166534", fontWeight: "bold", display: "inline-flex", alignItems: "center", gap: 4 },
    menuWrap: { position: "absolute", top: 18, right: 18, zIndex: 10 },
    dotsBtn: { background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#aaa", padding: "0 4px", lineHeight: 1, borderRadius: 6 },
    dropdown: { position: "absolute", right: 0, top: "110%", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 140, zIndex: 100, overflow: "hidden" },
    dropItem: { display: "block", width: "100%", padding: "10px 16px", background: "none", border: "none", textAlign: "left", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#333" },
};
